import { Component, OnDestroy, OnInit } from "@angular/core";
import { Store } from "@ngrx/store";

import { BehaviorSubject, Subscription } from "rxjs";
import { filter, first, take } from "rxjs/operators";

import { isSuccess, isFailure } from "@nll/datum/DatumEither";
import { notNil } from "@qqcw/qqsystem-util";
import { filterSuccessMapToRightValue } from "src/lib/datum-either";

import {
  accountLink,
  accountLookupLast4Plate,
  clearAccountLookup,
  accountLookup,
  getAllRegions,
  newAccount,
  selectAccountLookup,
  selectLinkAccount,
  selectNewAccount,
  selectProfile,
  selectRegions,
  clearNewAccount,
  clearAccountInfo,
  getProfile,
  accountRelink,
  clearProfile,
} from "src/app/core/ngrx/myqq";
import { PaymentMethodResult } from "src/app/shared/modules/stripe/stripe-definitions/payment-method";
import {
  Account,
  AccountCheckLast4PlateRequest,
} from "src/app/core/services/myqq";
import { log, LogLevels } from "src/app/core/ngrx/logger";
import { errors } from "src/app/core/services/myqq/myqq.errors";
import {
  AccountSetupSteps,
  b2cSignUpDetails,
  selectAccountSetupStep,
  selectB2cSignUpDetails,
  selectExistingMembership,
  selectSalesPipeline,
  setAccountSetupStep,
  setExistingMembership,
} from "src/app/core/ngrx/ui";
import { Router } from "@angular/router";

@Component({
  selector: "myqq-account-setup",
  templateUrl: "./account-setup.page.html",
  styleUrls: ["./account-setup.page.scss"],
})
export class AccountSetupPage implements OnInit, OnDestroy {
  constructor(readonly store$: Store<any>, readonly router: Router) {}
  readonly salesPipeline$ = this.store$.select(selectSalesPipeline);
  readonly accountLookup$ = this.store$.select(selectAccountLookup);
  readonly profileData$ = this.store$.select(selectProfile);
  readonly linkAccount$ = this.store$.select(selectLinkAccount);
  readonly regions$ = this.store$.select(selectRegions);
  readonly currentStep$ = this.store$.select(selectAccountSetupStep);
  readonly isRelink = this.router.url === "/relink";
  readonly steps = AccountSetupSteps;
  readonly newAccountSetUpFailureMessage =
    "Unfortunately, we could not create an account currently.";
  readonly emailAlreadyOnMembershipMessage = {
    prefix: "This email address",
    message:
      " is already associated with an active Unlimited Membership. Click below to link your myQQ account to your membership!",
  };
  pendingAccount?: Account;
  readonly saveNewAccountSub: Subscription = this.store$
    .select(selectNewAccount)
    .pipe(filter((value) => isFailure(value) || isSuccess(value)))
    .subscribe((result) => {
      if (isSuccess(result)) {
        let redirectUri = "/purchase";
        //create subject
        let sub = new BehaviorSubject<b2cSignUpDetails>(null);
        this.store$.select(selectB2cSignUpDetails).subscribe(sub);
        if (!!sub && !!sub.value && notNil(sub.value.benefitcode)) {
          redirectUri = "/employeebenefits";
          this.store$.dispatch(clearProfile(null));
        }
        this.store$.dispatch(clearAccountInfo(null));
        this.router.navigateByUrl(redirectUri);
      } else if (isFailure(result)) {
        const error: any = result.value?.left;
        switch (error?.error?.message) {
          case errors.myqqApi.errGetStore:
            this.handleHomeStoreNotFound();
            break;
          case errors.myqqApi.errEmailOnMembership:
            this.handleEmailOnAnotherMembership(error);

            break;
          default:
            this.gotoStep(this.steps.failed);

            break;
        }
      }
    });
  readonly linkAccountSub: Subscription = this.store$
    .select(selectLinkAccount)
    .pipe(filterSuccessMapToRightValue, first())
    .subscribe(() => this.store$.dispatch(clearAccountInfo(null)));

  profileSub: Subscription;
  existingMembershipSub: Subscription;
  emailFromProfileSub: Subscription;

  mustLink = false;
  ngOnInit() {
    this.store$.dispatch(getAllRegions.pending(null));
    this.handleExistingAccount();
    this.handleIHaveMembership();
  }

  ngOnDestroy() {
    this.profileSub?.unsubscribe();
    this.saveNewAccountSub?.unsubscribe();
    this.linkAccountSub?.unsubscribe();
    this.existingMembershipSub?.unsubscribe();
    this.emailFromProfileSub?.unsubscribe();
    this.store$.dispatch(clearNewAccount(null));
    this.store$.dispatch(clearAccountLookup(null));
    this.store$.dispatch(setExistingMembership({ hasMembership: null }));
  }

  gotoStep(step: AccountSetupSteps) {
    this.store$.dispatch(setAccountSetupStep(step));
  }

  retryRegionalData() {
    this.store$.dispatch(getAllRegions.pending(null));
  }

  handleHomeStoreNotFound() {
    this.store$.dispatch(clearNewAccount(null));
    this.gotoStep(this.steps.choose_location);
  }

  handleEmailOnAnotherMembership(error: Error) {
    this.profileData$
      .pipe(take(1), filterSuccessMapToRightValue)
      .subscribe((p) => {
        const email = p.profile?.userName;
        if (email) {
          this.emailAlreadyOnMembershipMessage.prefix = `The email address ${email}`;
        }
      });
    this.store$.dispatch(clearNewAccount(null));
    this.mustLink = true;
    this.gotoStep(this.steps.failed);

    this.store$.dispatch(
      log({
        level: LogLevels.WARNING,
        data: {
          error: error,
        },
        alert: true, // For now, probably remove after determining how common this is.
        message:
          "Tried to create new account with an email already on a membership.",
      })
    );
  }

  handleConfirmAccountLink(account: Account) {
    if (this.isRelink) {
      this.store$.dispatch(accountRelink.pending(account.personId));
    } else {
      this.store$.dispatch(accountLink.pending(account.personId));
    }
    this.gotoStep(this.steps.complete);
  }

  startOver() {
    this.router.navigateByUrl("/");
    this.store$.dispatch(clearNewAccount(null));
    this.store$.dispatch(clearAccountLookup(null));
    this.store$.dispatch(setExistingMembership({ hasMembership: null }));
    this.store$.dispatch(clearAccountInfo(null));
  }

  handleLookupError(error: { message: string; error: Error; data: any }) {
    this.store$.dispatch(
      log({
        level: LogLevels.ERROR,
        data: {
          metadata: error.data,
          error: error.error,
        },
        message: error.message,
      })
    );
    this.gotoStep(this.steps.failed);
  }

  createNewAccount(account: Account) {
    this.pendingAccount = account;
    this.store$.dispatch(newAccount.pending(account));
  }

  handleHasAccount() {
    this.store$.dispatch(clearAccountLookup(null));
    return this.gotoStep(this.steps.last4lookup);
  }

  handleStartOverNewAccount() {
    this.store$.dispatch(clearNewAccount(null));
    return this.gotoStep(this.steps.enter_account_details);
  }

  searchForPaymentMethod(result: PaymentMethodResult) {
    if (notNil(result.paymentMethod)) {
      this.store$.dispatch(accountLookup.pending(result.paymentMethod));
      return this.gotoStep(this.steps.confirm_account);
    }
  }

  last4Lookup(lookupData: AccountCheckLast4PlateRequest) {
    this.store$.dispatch(accountLookupLast4Plate.pending(lookupData));
    this.gotoStep(this.steps.confirm_account);
  }

  handleExistingAccount() {
    this.profileSub = this.store$
      .select(selectProfile)
      .pipe(filterSuccessMapToRightValue, first())
      .subscribe(({ membership }) =>
        this.isRelink
          ? this.handleRelinking()
          : notNil(membership)
          ? this.startOver()
          : null
      );
    this.store$.dispatch(getProfile.pending(null));
  }

  handleIHaveMembership() {
    this.existingMembershipSub = this.store$
      .select(selectExistingMembership)
      .pipe(first())
      .subscribe((value) =>
        value.hasMembership ? this.gotoStep(this.steps.last4lookup) : null
      );
  }

  handleRelinking() {
    this.gotoStep(this.steps.last4lookup);
  }
}
